import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, ChevronDown, X, Phone } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api, imgUrl, rupee } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";

const CLOTHING_FALLBACK = [
  { name: "Kurtis", slug: "kurtis" }, { name: "Dresses", slug: "dresses" },
  { name: "Tops", slug: "tops" }, { name: "Co-ord Sets", slug: "co-ord-sets" },
  { name: "Traditional Wear", slug: "traditional-wear" },
];
const FOOTWEAR_FALLBACK = [
  { name: "Sandals", slug: "sandals" }, { name: "Heels", slug: "heels" },
  { name: "Flats", slug: "flats" }, { name: "Casual Footwear", slug: "casual-footwear" },
];

const AnnouncementBar = () => {
  const { settings } = useStore();
  const items = settings.announcements || ["Free Shipping on Orders Above ₹999", "Easy Returns & Exchange", "100% Original Products"];
  return (
    <div className="bg-blush text-charcoal" data-testid="announcement-bar">
      <div className="mx-auto hidden max-w-7xl items-center justify-between px-6 py-2 text-xs md:flex">
        {items.map((t, i) => <span key={i} className="tracking-wide">{t}</span>)}
      </div>
      <div className="overflow-hidden py-2 md:hidden">
        <div className="marquee-track text-xs">
          {[...items, ...items].map((t, i) => <span key={i} className="mx-6 tracking-wide">{t}</span>)}
        </div>
      </div>
    </div>
  );
};

const Wordmark = ({ small }) => <Logo size={small ? "sm" : "md"} />

const SearchBox = ({ onClose }) => {
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setSugg([]); return; }
    const t = setTimeout(() => api.get(`/search/suggestions?q=${encodeURIComponent(q)}`).then((r) => setSugg(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const submit = (e) => { e.preventDefault(); if (q.trim()) { navigate(`/search?q=${encodeURIComponent(q)}`); onClose(); } };
  return (
    <div className="border-b border-border bg-white" data-testid="search-panel">
      <div className="mx-auto max-w-3xl px-6 py-6">
        <form onSubmit={submit} className="flex items-center gap-2">
          <Search className="h-5 w-5 text-muted-text" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for kurtis, dresses, footwear..."
            data-testid="search-input" className="rounded-none border-0 border-b border-border focus-visible:ring-0 text-base" />
          <button type="button" onClick={onClose} data-testid="search-close"><X className="h-5 w-5 text-muted-text" /></button>
        </form>
        {sugg.length > 0 && (
          <div className="mt-4 space-y-1">
            {sugg.map((s) => (
              <Link key={s.slug} to={`/product/${s.slug}`} onClick={onClose} className="flex items-center gap-3 rounded-sm p-2 hover:bg-cream" data-testid={`suggestion-${s.slug}`}>
                <img src={imgUrl(s.primary_image)} alt={s.name} className="h-12 w-10 object-cover" />
                <span className="flex-1 text-sm">{s.name}</span>
                <span className="text-sm font-medium text-burgundy">{rupee(s.price)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const NavDropdown = ({ label, items, base }) => (
  <div className="group relative">
    <button className="flex items-center gap-1 py-2 text-sm font-medium text-charcoal transition-colors hover:text-burgundy" data-testid={`nav-${label.toLowerCase()}`}>
      {label} <ChevronDown className="h-3.5 w-3.5" />
    </button>
    <div className={`invisible absolute left-1/2 top-full z-50 -translate-x-1/2 border border-border bg-white opacity-0 shadow-md transition-opacity duration-200 group-hover:visible group-hover:opacity-100 ${items.length > 7 ? "grid w-[26rem] grid-cols-2 p-2" : "w-52"}`}>
      {items.map((it) => (
        <Link key={it.slug} to={`/collections/${it.slug}`} className="block px-4 py-2.5 text-sm text-charcoal hover:bg-cream hover:text-burgundy" data-testid={`nav-item-${it.slug}`}>
          {it.name}
        </Link>
      ))}
    </div>
  </div>
);

const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartSubtotal, cartCount } = useStore();
  const navigate = useNavigate();
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-drawer">
        <h2 className="font-serif text-2xl text-burgundy">Your Cart ({cartCount})</h2>
        {cart.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <ShoppingBag className="h-12 w-12 text-muted-text/40" />
            <p className="text-muted-text">Your cart is empty</p>
            <Button onClick={() => { setCartOpen(false); navigate("/new-arrivals"); }} className="rounded-none bg-burgundy" data-testid="cart-shop-btn">Start Shopping</Button>
          </div>
        ) : (
          <>
            <div className="-mx-6 flex-1 overflow-y-auto px-6">
              {cart.map((i) => (
                <div key={i.key} className="flex gap-3 border-b border-border py-4" data-testid={`cart-item-${i.slug}`}>
                  <img src={imgUrl(i.image)} alt={i.name} className="h-24 w-20 object-cover" />
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm font-medium">{i.name}</p>
                    <p className="text-xs text-muted-text">{[i.size, i.color].filter(Boolean).join(" · ")}</p>
                    <p className="mt-1 text-sm font-semibold text-burgundy">{rupee(i.price)}</p>
                    <div className="mt-auto flex items-center gap-2">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} className="h-7 w-7 border border-border" data-testid={`cart-dec-${i.slug}`}>-</button>
                      <span className="w-6 text-center text-sm">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} className="h-7 w-7 border border-border" data-testid={`cart-inc-${i.slug}`}>+</button>
                      <button onClick={() => removeFromCart(i.key)} className="ml-auto text-xs text-muted-text hover:text-burgundy" data-testid={`cart-remove-${i.slug}`}>Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-semibold">{rupee(cartSubtotal)}</span></div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => { setCartOpen(false); navigate("/cart"); }} className="rounded-none" data-testid="view-cart-btn">View Cart</Button>
                <Button onClick={() => { setCartOpen(false); navigate("/checkout"); }} className="rounded-none bg-burgundy" data-testid="checkout-btn">Checkout</Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Header = () => {
  const { cartCount, wishlist, user, setCartOpen, settings, categories } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const clothing = categories.filter((c) => c.group === "clothing");
  const footwear = categories.filter((c) => c.group === "footwear");
  const CLOTHING = clothing.length ? clothing : CLOTHING_FALLBACK;
  const FOOTWEAR = footwear.length ? footwear : FOOTWEAR_FALLBACK;

  const nav = (path) => { setMobileOpen(false); navigate(path); };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md">
      <AnnouncementBar />
      <div className="border-b border-border">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 md:px-6">
          {/* Mobile menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="h-6 w-6 text-charcoal" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto" data-testid="mobile-menu">
                <Wordmark small />
                <nav className="mt-6 flex flex-col">
                  <button onClick={() => nav("/")} className="border-b border-border py-3 text-left text-sm">Home</button>
                  <Accordion type="single" collapsible>
                    <AccordionItem value="clothing" className="border-border">
                      <AccordionTrigger className="py-3 text-sm">Clothing</AccordionTrigger>
                      <AccordionContent>
                        {CLOTHING.map((c) => <button key={c.slug} onClick={() => nav(`/collections/${c.slug}`)} className="block w-full py-2 pl-3 text-left text-sm text-muted-text">{c.name}</button>)}
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="footwear" className="border-border">
                      <AccordionTrigger className="py-3 text-sm">Footwear</AccordionTrigger>
                      <AccordionContent>
                        {FOOTWEAR.map((c) => <button key={c.slug} onClick={() => nav(`/collections/${c.slug}`)} className="block w-full py-2 pl-3 text-left text-sm text-muted-text">{c.name}</button>)}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <button onClick={() => nav("/new-arrivals")} className="border-b border-border py-3 text-left text-sm">New Arrivals</button>
                  <button onClick={() => nav("/offers")} className="border-b border-border py-3 text-left text-sm">Offers</button>
                  <button onClick={() => nav("/about")} className="border-b border-border py-3 text-left text-sm">About Us</button>
                  <button onClick={() => nav("/contact")} className="border-b border-border py-3 text-left text-sm">Contact</button>
                </nav>
                <div className="mt-6 flex flex-col gap-2">
                  <a href={`tel:${(settings.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-burgundy"><Phone className="h-4 w-4" /> {settings.phone}</a>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="lg:flex-none"><Wordmark /></div>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="main-nav">
            <Link to="/" className="text-sm font-medium text-charcoal hover:text-burgundy" data-testid="nav-home">Home</Link>
            <NavDropdown label="Clothing" items={CLOTHING} />
            <NavDropdown label="Footwear" items={FOOTWEAR} />
            <Link to="/new-arrivals" className="text-sm font-medium text-charcoal hover:text-burgundy" data-testid="nav-new">New Arrivals</Link>
            <Link to="/offers" className="text-sm font-medium text-charcoal hover:text-burgundy" data-testid="nav-offers">Offers</Link>
            <Link to="/about" className="text-sm font-medium text-charcoal hover:text-burgundy" data-testid="nav-about">About Us</Link>
            <Link to="/contact" className="text-sm font-medium text-charcoal hover:text-burgundy" data-testid="nav-contact">Contact</Link>
          </nav>

          <div className="flex items-center gap-4">
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="search-toggle" aria-label="Search"><Search className="h-5 w-5 text-charcoal hover:text-burgundy" /></button>
            <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="Account"><User className="h-5 w-5 text-charcoal hover:text-burgundy" /></Link>
            <Link to="/wishlist" className="relative hidden sm:block" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart className="h-5 w-5 text-charcoal hover:text-burgundy" />
              {wishlist.length > 0 && <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-burgundy text-[10px] text-white">{wishlist.length}</span>}
            </Link>
            <button onClick={() => setCartOpen(true)} className="relative" data-testid="cart-toggle" aria-label="Cart">
              <ShoppingBag className="h-5 w-5 text-charcoal hover:text-burgundy" />
              {cartCount > 0 && <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-burgundy text-[10px] text-white" data-testid="cart-count">{cartCount}</span>}
            </button>
          </div>
        </div>
      </div>
      {searchOpen && <SearchBox onClose={() => setSearchOpen(false)} />}
      <CartDrawer />
    </header>
  );
};

export default Header;
